import React, { useEffect, useState } from "react";
import { api, mediaUrl, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarClock, Image as ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function SchedulerPage() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [delay, setDelay] = useState(120);
  const [threads, setThreads] = useState(1);
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [scheduled, setScheduled] = useState([]);

  useEffect(() => {
    api.get("/groups").then((r) => setGroups(r.data));
    loadScheduled();
  }, []);

  const loadScheduled = async () => {
    const { data } = await api.get("/campaigns");
    setScheduled(
      data.filter(
        (c) => c.scheduled_at && (c.status === "queued" || c.status === "running"),
      ),
    );
  };

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(data.url);
    } finally {
      setUploading(false);
    }
  };

  const schedule = async () => {
    if (!title.trim()) return toast.error("Digite a descrição");
    if (!scheduledAt) return toast.error("Escolha data e hora");
    if (selected.size === 0) return toast.error("Selecione ao menos 1 destino");

    const when = new Date(scheduledAt);
    if (when <= new Date()) return toast.error("Data deve ser no futuro");

    setBusy(true);
    try {
      const { data: camp } = await api.post("/campaigns", {
        title,
        image_url: imageUrl || null,
        group_ids: Array.from(selected),
        delay_seconds: Number(delay),
        threads: Number(threads),
        scheduled_at: when.toISOString(),
      });
      await api.post(`/campaigns/${camp.id}/start`);
      toast.success(`Agendado para ${when.toLocaleString("pt-BR")}`);
      setTitle("");
      setImageUrl("");
      setSelected(new Set());
      setScheduledAt("");
      loadScheduled();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao agendar"));
    } finally {
      setBusy(false);
    }
  };

  const minDateTime = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset() + 5);
    return d.toISOString().slice(0, 16);
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]">
      <div className="mb-8">
        <p className="font-mono-label text-zinc-500 mb-1">Automação futura</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">Agendamento</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Prepare suas divulgações para disparar automaticamente em data e hora específicas.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-6 space-y-5">
          <div>
            <Label className="font-mono-label text-zinc-500 mb-2 block">
              Data e hora do disparo
            </Label>
            <div className="relative">
              <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              <Input
                type="datetime-local"
                value={scheduledAt}
                min={minDateTime()}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="scheduler-datetime-input"
                className="pl-10 rounded-sm h-11"
              />
            </div>
          </div>

          <div>
            <Label className="font-mono-label text-zinc-500 mb-2 block">
              Texto da publicação
            </Label>
            <Textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Digite o conteúdo..."
              data-testid="scheduler-title-input"
              className="min-h-[140px] rounded-sm"
            />
          </div>

          <div>
            <Label className="font-mono-label text-zinc-500 mb-2 block">Imagem (opcional)</Label>
            {imageUrl ? (
              <div className="relative inline-block">
                <img src={mediaUrl(imageUrl)} className="max-h-40 rounded-sm border border-zinc-200" alt="" />
                <button
                  onClick={() => setImageUrl("")}
                  className="absolute top-2 right-2 h-7 w-7 bg-zinc-900/80 text-white rounded-sm flex items-center justify-center"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 h-24 border-2 border-dashed border-zinc-200 rounded-sm cursor-pointer hover:bg-zinc-50">
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="scheduler-upload-input" />
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ImageIcon className="h-5 w-5 text-zinc-400" /><span className="text-sm text-zinc-600">Adicionar foto</span></>}
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono-label text-zinc-500 mb-2 block">Thread</Label>
              <Input type="number" min={1} max={5} value={threads} onChange={(e) => setThreads(e.target.value)} data-testid="scheduler-threads-input" className="rounded-sm h-11" />
            </div>
            <div>
              <Label className="font-mono-label text-zinc-500 mb-2 block">Delay (seg, mín 60)</Label>
              <Input type="number" min={60} value={delay} onChange={(e) => setDelay(e.target.value)} data-testid="scheduler-delay-input" className="rounded-sm h-11" />
            </div>
          </div>

          <Button
            onClick={schedule}
            disabled={busy}
            data-testid="scheduler-submit-button"
            className="w-full h-12 rounded-sm bg-blue-600 hover:bg-blue-700 font-bold"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
            AGENDAR · {selected.size} DESTINOS
          </Button>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <p className="font-mono-label text-zinc-500 mb-3">
            Destinos ({selected.size}/{groups.length})
          </p>
          <div className="max-h-[520px] overflow-y-auto scroll-thin divide-y divide-zinc-100">
            {groups.map((g) => (
              <label
                key={g.id}
                className="flex items-center gap-3 py-2.5 cursor-pointer"
                data-testid={`scheduler-group-${g.id}`}
              >
                <Checkbox
                  checked={selected.has(g.id)}
                  onCheckedChange={() => toggle(g.id)}
                />
                <div className="h-8 w-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold">{g.name[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{g.name}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {scheduled.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-xl font-semibold mb-4">Agendamentos ativos</h2>
          <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100">
            {scheduled.map((c) => (
              <div key={c.id} className="p-4 flex items-center gap-4">
                <CalendarClock className="h-5 w-5 text-blue-600" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{c.title.split("\n")[0]}</div>
                  <div className="text-xs text-zinc-500 font-mono mt-0.5">
                    {new Date(c.scheduled_at).toLocaleString("pt-BR")} · {c.total} destinos
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
